import Box from '@luce/ui-kit/components/base/Box'
import Color from '@luce/ui-kit/components/base/Color'
import Typography from '@luce/ui-kit/components/base/Typography'
import Button from '@luce/ui-kit/components/button'
import Icon from '@luce/ui-kit/components/icons/Icons'
import Dialog from '@luce/ui-kit/components/organism/Dialog'
import { makeStyles, Theme } from '@luce/ui-kit/themes/ThemeProvider'
import React from 'react'
import { useDeleteWorkerTip, DeleteWorkerTipResult } from '../gqlhooks'
import { useSnackbar } from 'notistack'

interface Props {
  workerTipId: string
  onCancel(): void
  onSuccessDeleted(): void
}

const useStyles = makeStyles((theme: Theme) => ({
  mDialogPaper: {
    '& .MuiDialog-paper': {
      padding: theme.spacing(3),
      margin: theme.spacing(2)
    }
  },
  iconLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: Color.primary[300],
    borderRadius: '50%',
    marginBottom: theme.spacing(2),
    color: theme.palette.warning.main
  },
  bodyText: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: 10,
    alignItems: 'center',
    justifyContent: 'center'
  },
  actionCancelBtn: {
    width: 175,
    color: theme.palette.primary.main,
    background: Color.primary[300],
    '&:hover': {
      background: Color.primary[300]
    }
  },
  actionProcessBtn: {
    width: 175,
    color: theme.palette.common.white,
    background: theme.palette.primary.main,
    '&:hover': {
      background: theme.palette.primary.main
    }
  }
}))

const DeleteWorkerTipConfirmationModal: React.FC<Props> = (props) => {
  const { workerTipId, onCancel, onSuccessDeleted } = props
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [deleteWorkerTip, { loading }] = useDeleteWorkerTip()

  const handleConfirmDelete = async (): Promise<void> => {
    try {
      const result: DeleteWorkerTipResult = await deleteWorkerTip({
        id: workerTipId
      })
      if (result.data?.deleteWorkerTip?.workerTipDeleted) {
        enqueueSnackbar('Worker tip deleted successfully', {
          variant: 'success'
        })
        onSuccessDeleted()
      } else {
        enqueueSnackbar('Failed to delete worker tip', { variant: 'error' })
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Error deleting worker tip'
      enqueueSnackbar(errorMessage, { variant: 'error' })
    }
  }

  return (
    <Dialog maxWidth="xs" fullWidth open scroll="body">
      <Box>
        <Box className={classes.bodyText}>
          <Box className={classes.iconLabel}>
            <Icon iconName="WarningIcon" size="extraLarge" />
          </Box>
          <Typography variant="h3">Delete Worker Tip Confirmation</Typography>
          <Typography variant="body1" align="center">
            Are you sure you want to delete this worker tip? This action cannot
            be undone.
          </Typography>
        </Box>
      </Box>
      <br />
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        gridGap={12}
        pt={2}
      >
        <Button
          disabled={loading}
          className={classes.actionCancelBtn}
          variants="cta"
          onClick={handleConfirmDelete}
        >
          Yes
        </Button>
        <Button
          className={classes.actionProcessBtn}
          variants="cta"
          onClick={onCancel}
        >
          No
        </Button>
      </Box>
    </Dialog>
  )
}

export default DeleteWorkerTipConfirmationModal
